import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { queryUtils, useDeletePdpMutation } from '@vizit/api';
import type { ImageType } from '@vizit/api';
import { PdpStatus } from '@vizit/api';
import { AssetGridViewer, AssetTableFilterOptions, AssetTableFilters, AssetTableProvider } from '@vizit/assets';
import { AssetUploadDialog } from '@vizit/assets';
import { AlertLevel, SegmentedControl, Separator, Tab, TabList, TabPanel, TabSize, TabVariant } from '@vizit/atoms';
import { Tabs, useToast } from '@vizit/atoms';
import { BenchmarkGrid, type BenchmarkGridFilterOptions, type BenchmarkGridFilters } from '@vizit/collections';
import { BenchmarkNewDialog, CollectionGridFilterOptions, CollectionGridFilters } from '@vizit/collections';
import { CollectionGridProvider, CollectionGridViewer, CollectionNewDialog } from '@vizit/collections';
import { CollectionSortOption } from '@vizit/collections';
import { useCollectionMenuActions, useCollectionsViewCounts } from '@vizit/collections';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import { useCustomBenchmarksAvailability, useShowActionsBar } from '@vizit/feature-flags';
import { RefreshCw04Icon, Trash01Icon } from '@vizit/icons';
import type { ProductListingData, RowAction } from '@vizit/molecules';
import { ThumbnailSizeSlider } from '@vizit/molecules';
import { BenchmarkCardGrid, type ScoreLevel, parseTagIdsParam, useThumbnailSlider } from '@vizit/organisms';
import { DeleteProductDialog, RefreshProductDialog, isRefreshAllowed } from '@vizit/products';
import { ProductBulkImportDialog, ProductImportDialog, ProductNewDialog } from '@vizit/products';
import { ProductTableFilterOptions, ProductTableFilters, ProductTableProvider } from '@vizit/products';
import { ProductTableViewerVirtualized } from '@vizit/products';
import { cx } from '@vizit/theme';
import * as React from 'react';
import type { PressEvent } from 'react-aria';
import { useWorkspaceSlug } from '../../../hooks/use-workspace-link';
import { LIBRARY_BREADCRUMB, getLibraryBreadcrumb, navigateWithState } from '../../../router/route-utils';
import { getBenchmarksBreadcrumbTrail } from '../../../router/route-utils';
import { usePage } from '../../providers';

enum LibraryTabOptions {
  Assets = 'assets',
  Products = 'products',
  Collections = 'collections',
}

/** The two views switched between inside the Collections tab. */
enum CollectionsViewOptions {
  Collections = 'collections',
  Benchmarks = 'benchmarks',
}

interface LibraryPageProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Additional CSS classes to apply to the component.
   *
   * @optional
   * @type string
   */
  className?: string;
  /**
   * The currently selected tab
   * @required
   * @type {LibraryTabOptions}
   */
  selectedTab: LibraryTabOptions;
  /**
   * The ID of the portfolio to fetch assets for
   * @optional
   * @type {string}
   */
  workspaceId?: string;
  /**
   * The product table filters
   * @optional
   * @type {Record<ProductTableFilterOptions, string>}
   */
  productTableFilters?: Record<ProductTableFilterOptions, string>;
  /**
   * The asset table filters
   * @optional
   * @type {Record<AssetTableFilterOptions, string>}
   */
  assetTableFilters?: Record<AssetTableFilterOptions, string>;
  /**
   * The collection grid filters
   * @optional
   */
  collectionGridFilters?: Partial<Record<CollectionGridFilterOptions, string>>;
  /**
   * The ID of the organization to use for fetching images
   * @optional
   * @type {string}
   */
  organizationId?: string;
  /**
   * The function to call when the tab changes
   * @required
   * @type {(key: LibraryTabOptions) => Promise<void>}
   * @param key - The key of the tab to change to
   */
  onTabChange: (key: LibraryTabOptions) => Promise<void>;
  /**
   * The function to call when the product table filters change
   * @optional
   * @type {(filter: ProductTableFilterOptions, value: string) => void}
   * @param filter - The filter option to change
   * @param value - The new value for the filter
   */
  onProductTableFilterChange?: (filter: ProductTableFilterOptions, value?: string) => void;
  /**
   * The function to call when the asset table filters change
   * @optional
   * @type {(filter: AssetTableFilterOptions, value: string) => void}
   * @param filter - The filter option to change
   * @param value - The new value for the filter
   */
  onAssetTableFilterChange?: (filter: AssetTableFilterOptions, value: string) => void;
  onCollectionGridFilterChange?: (filter: CollectionGridFilterOptions, value?: string) => void;
  /**
   * The view shown inside the Collections tab. Ignored while custom benchmarks are off.
   * @optional
   * @default CollectionsViewOptions.Collections
   */
  collectionsView?: CollectionsViewOptions;
  /**
   * Called when the Collections / Benchmarks switch changes
   * @optional
   */
  onCollectionsViewChange?: (view: CollectionsViewOptions) => void;
  /**
   * The Benchmarks view's search, category and sort filters, kept in the URL
   * @optional
   */
  benchmarkGridFilters?: BenchmarkGridFilters;
  /**
   * Called when a Benchmarks view filter changes; `undefined` clears it
   * @optional
   */
  onBenchmarkGridFilterChange?: (filter: BenchmarkGridFilterOptions, value?: string) => void;
  /**
   * The function to call when the image is uploaded
   *
   * @param id - The ID of the image
   */
  onSuccessfulUpload: (id: string) => void;
  /**
   * The function to call when a PDP is successfully submitted
   *
   * @param pdpId - The ID of the PDP
   */
  onSuccessfulImport: (pdpId: string) => void;
}

/**
 * Main library page that displays the user's asset library.
 * This page will contain all uploaded assets and products.
 *
 * @param props - The component props
 * @param props.className - Additional CSS classes to apply
 * @param props.selectedTab - The currently selected tab
 * @param props.workspaceId - The ID of the portfolio to fetch assets for
 * @param props.productTableFilters - The product table filters
 * @param props.assetTableFilters - The asset table filters
 * @param props.organizationId - The ID of the organization to use for fetching images
 * @param props.onTabChange - The function to call when the tab changes
 * @param props.onProductTableFilterChange - The function to call when product table filters change
 * @param props.onAssetTableFilterChange - The function to call when asset table filters change
 * @param props.onSuccessfulUpload - The function to call when an image is uploaded
 * @param props.onSuccessfulImport - The function to call when a PDP is successfully imported
 *
 * @example
 * <LibraryPage selectedTab={LibraryTabOptions.Assets} workspaceId="123" organizationId="org-123" onTabChange={() => {}} onSuccessfulUpload={() => {}} onSuccessfulImport={() => {}} />
 */
function LibraryPage(props: LibraryPageProps) {
  const {
    className,
    selectedTab,
    workspaceId,
    productTableFilters = {},
    assetTableFilters = {},
    collectionGridFilters = {},
    organizationId,
    onTabChange,
    onSuccessfulUpload,
    onSuccessfulImport,
    onProductTableFilterChange,
    onAssetTableFilterChange,
    onCollectionGridFilterChange,
    collectionsView = CollectionsViewOptions.Collections,
    onCollectionsViewChange,
    benchmarkGridFilters,
    onBenchmarkGridFilterChange,
    ...rest
  } = props;

  const isCollectionsTab = selectedTab === LibraryTabOptions.Collections;
  // Only the Collections tab shows benchmarks, so the other tabs skip the API check and the count requests.
  const benchmarksAvailability = useCustomBenchmarksAvailability(isCollectionsTab ? workspaceId : undefined);
  const showCustomBenchmarks = benchmarksAvailability.isEnabled;
  const isBenchmarksViewRequested = collectionsView === CollectionsViewOptions.Benchmarks;
  const isBenchmarksView = showCustomBenchmarks && isBenchmarksViewRequested;
  const isCheckingBenchmarksView = benchmarksAvailability.isChecking && isBenchmarksViewRequested;
  // Benchmarks name the category a collection is scored against, so its tiles drop theirs. While the API check runs
  // the tiles show none, rather than one that may go a moment later.
  const showCollectionCategory = !showCustomBenchmarks && !benchmarksAvailability.isChecking;

  const { collectionCount, benchmarkCount } = useCollectionsViewCounts({
    workspaceId,
    isEnabled: showCustomBenchmarks && isCollectionsTab,
  });

  const router = useRouter();
  const workspaceSlug = useWorkspaceSlug();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const { onAuthenticationError } = usePage();

  const showActionsBar = useShowActionsBar();
  // Same rules as on the Collection page (breakpoint, upload queue), shared in the hook
  const thumbnailSlider = useThumbnailSlider('assets', { isEnabled: showActionsBar });

  const [actionModal, setActionModal] = React.useState<React.ReactNode>(null);

  const closeActionModal = React.useCallback(() => {
    setActionModal((prev) => {
      if (prev && React.isValidElement(prev)) {
        return React.cloneElement(prev as React.ReactElement<{ isOpen: boolean }>, { isOpen: false });
      }
      return prev;
    });
    setTimeout(() => setActionModal(null), 300);
  }, []);

  const deletePdpMutation = useDeletePdpMutation({
    onSuccess: async () => {
      await queryUtils.invalidateAllPdpLists(queryClient);
      closeActionModal();
    },
    onError: (error) => {
      addToast({
        title: 'Failed to delete product',
        description: 'An error occurred while deleting the product',
        level: AlertLevel.Error,
      });
      Sentry.captureException(error, {
        level: 'error',
        tags: {
          feature: SENTRY_FEATURE_TAGS.Product,
          stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Product].productDelete,
        },
      });
    },
  });

  const handleDeleteProduct = React.useCallback(
    async (pdpId: string) => {
      if (!workspaceId) {
        addToast({
          title: 'Failed to delete product page',
          description: 'Portfolio ID is required',
          level: AlertLevel.Error,
        });

        Sentry.captureException(new Error('Portfolio ID is required'), {
          level: 'error',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Product,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Product].productDelete,
          },
        });
        return;
      }

      await deletePdpMutation.mutateAsync({
        workspace_id: workspaceId,
        pdp_id: pdpId,
      });
    },
    [workspaceId, deletePdpMutation, addToast],
  );

  const handleDeleteClick = React.useCallback(
    (item: ProductListingData) => {
      const handleOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
          closeActionModal();
        }
      };

      setActionModal(
        <DeleteProductDialog
          isOpen={true}
          onOpenChange={handleOpenChange}
          productId={item.id}
          onDelete={handleDeleteProduct}
        />,
      );
    },
    [handleDeleteProduct, closeActionModal],
  );

  const handleRefreshPdp = React.useCallback(
    (item: ProductListingData) => {
      if (!workspaceId) return;

      const handleOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
          closeActionModal();
        }
      };

      setActionModal(
        <RefreshProductDialog
          workspaceId={workspaceId}
          productId={item.id}
          retailer={item.retailer}
          isOpen={true}
          onOpenChange={handleOpenChange}
        />,
      );
    },
    [workspaceId, closeActionModal],
  );

  const rowActions = React.useMemo<RowAction[]>(
    () =>
      [
        {
          id: 'refresh',
          icon: <RefreshCw04Icon size={20} className="text-black" />,
          label: 'Refresh PDP for',
          onAction: handleRefreshPdp,
          tooltip: (item: ProductListingData) =>
            item.status === PdpStatus.ScrapeStarted ? 'refreshing...' : 'Refresh page data',
          isDisabled: (item: ProductListingData) => !isRefreshAllowed(item),
          isHidden: (item: ProductListingData) => item.sourceType === 'manual',
        },
        {
          id: 'delete',
          icon: <Trash01Icon size={20} />,
          label: 'Delete',
          onAction: handleDeleteClick,
          tooltip: 'Delete page',
        },
      ].filter(Boolean) as RowAction[],
    [handleDeleteClick, handleRefreshPdp],
  );

  const handleNavigateToAsset = React.useCallback(
    (assetId: string) => {
      navigateWithState(router, {
        to: '/w/$workspaceSlug/library/assets/$assetId',
        params: { workspaceSlug, assetId },
        search: { origin: 'library' },
        from: { trail: [{ ...getLibraryBreadcrumb('assets'), search: { view: selectedTab, ...assetTableFilters } }] },
      });
    },
    [router, selectedTab, assetTableFilters, workspaceSlug],
  );

  const handleNavigateToProduct = React.useCallback(
    (pdpId: string) => {
      navigateWithState(router, {
        to: '/w/$workspaceSlug/library/products/$pdpId',
        params: { workspaceSlug, pdpId },
        search: { products: 'open' },
        from: {
          // Reads "Library", not the tab name: a product page is reached from
          // places other than this tab, so the crumb stays stable across them.
          // The search still carries the tab and its filters, so following it
          // back lands on the Product Pages tab exactly as it was left.
          trail: [{ ...LIBRARY_BREADCRUMB, search: { view: selectedTab, ...productTableFilters } }],
        },
      });
    },
    [router, selectedTab, productTableFilters, workspaceSlug],
  );

  const collectionsSearchParams = React.useMemo(
    () => ({ view: selectedTab, ...collectionGridFilters }),
    [selectedTab, collectionGridFilters],
  );

  const handleNavigateToCollection = React.useCallback(
    (collectionId: string, _event: PressEvent) => {
      navigateWithState(router, {
        to: '/w/$workspaceSlug/library/collections/$collectionId',
        params: { workspaceSlug, collectionId },
        from: { trail: [{ ...getLibraryBreadcrumb('collections'), search: collectionsSearchParams }] },
      });
    },
    [router, collectionsSearchParams, workspaceSlug],
  );

  const getBenchmarkHref = React.useCallback(
    (benchmarkId: string) => `/w/${workspaceSlug}/library/benchmarks/${benchmarkId}`,
    [workspaceSlug],
  );

  const handleNavigateToBenchmark = React.useCallback(
    (benchmarkId: string) => {
      navigateWithState(router, {
        to: '/w/$workspaceSlug/library/benchmarks/$benchmarkId',
        params: { workspaceSlug, benchmarkId },
        // Following the Benchmarks crumb back lands on the Benchmarks view with its filters as they were.
        from: { trail: getBenchmarksBreadcrumbTrail(workspaceSlug, benchmarkGridFilters) },
      });
    },
    [router, workspaceSlug, benchmarkGridFilters],
  );

  const handleTabChange = React.useCallback(
    async (key: React.Key | null) => {
      const tab = key as LibraryTabOptions;
      await onTabChange(tab);
    },
    [onTabChange],
  );

  // Context: https://github.com/AdharkInc/vizit-v2-ui/pull/120#discussion_r2210748466
  // UI for Asset Grid Viewer panel is flickering skeletons on switching tabs
  const assetsPanel = React.useMemo(
    () => (
      <TabPanel id="assets" className="flex flex-col items-center flex-1 w-full mt-0">
        <div className="w-full max-w-screen-4xl mx-auto pt-12 px-8 max-sm:pt-8 max-sm:px-4">
          <AssetTableProvider
            workspaceId={workspaceId ?? undefined}
            productCategoryId={
              assetTableFilters && AssetTableFilterOptions.ProductCategory in assetTableFilters
                ? (assetTableFilters[AssetTableFilterOptions.ProductCategory] as string)
                : undefined
            }
            productCategoryName={
              assetTableFilters && AssetTableFilterOptions.ProductCategoryName in assetTableFilters
                ? (assetTableFilters[AssetTableFilterOptions.ProductCategoryName] as string)
                : undefined
            }
            imageType={
              assetTableFilters && AssetTableFilterOptions.ImageType in assetTableFilters
                ? (assetTableFilters[AssetTableFilterOptions.ImageType] as ImageType)
                : undefined
            }
            tagIds={
              assetTableFilters && AssetTableFilterOptions.Tags in assetTableFilters
                ? parseTagIdsParam(assetTableFilters[AssetTableFilterOptions.Tags])
                : undefined
            }
            onAssetTableFilterChange={onAssetTableFilterChange}
          >
            <AssetTableFilters
              organizationId={organizationId ?? undefined}
              onAuthenticationError={onAuthenticationError}
            />
            {/* Bottom padding keeps the last row clear of the floating slider */}
            <div
              className={cx('w-full max-h-[calc(100vh-150px)] overflow-y-auto', thumbnailSlider.isVisible && 'pb-36')}
            >
              <AssetGridViewer
                columns={thumbnailSlider.gridColumns}
                uploadAssetDialog={
                  <AssetUploadDialog
                    organizationId={organizationId ?? undefined}
                    workspaceId={workspaceId ?? undefined}
                    onSuccessfulUpload={onSuccessfulUpload}
                    onAuthenticationError={onAuthenticationError}
                  />
                }
                onNavigateToAsset={handleNavigateToAsset}
                className="w-full"
              />
            </div>
          </AssetTableProvider>
        </div>
        {/* Inside the panel, so it shows only on the Assets tab */}
        {thumbnailSlider.isVisible && (
          <ThumbnailSizeSlider
            columns={thumbnailSlider.columns}
            onColumnsChange={thumbnailSlider.setColumns}
            className="fixed bottom-12 right-12 z-30"
          />
        )}
      </TabPanel>
    ),
    [
      workspaceId,
      organizationId,
      assetTableFilters,
      thumbnailSlider,
      handleNavigateToAsset,
      onSuccessfulUpload,
      onAuthenticationError,
      onAssetTableFilterChange,
    ],
  );

  const productsPanel = React.useMemo(
    () => (
      <TabPanel id="products" className="flex flex-col items-center flex-1 w-full mt-0">
        <div className="max-h-screen overflow-auto w-full max-w-screen-4xl mx-auto pb-65 pt-12 px-8 max-sm:pt-8 max-sm:px-4">
          <ProductTableProvider
            organizationId={organizationId ?? ''}
            workspaceId={workspaceId ?? undefined}
            brand={
              productTableFilters && ProductTableFilterOptions.Brand in productTableFilters
                ? (productTableFilters[ProductTableFilterOptions.Brand] as string)
                : undefined
            }
            listingScoreLevel={
              productTableFilters && ProductTableFilterOptions.ListingScoreLevel in productTableFilters
                ? (productTableFilters[ProductTableFilterOptions.ListingScoreLevel] as ScoreLevel)
                : undefined
            }
            productCategoryId={
              productTableFilters && ProductTableFilterOptions.ProductCategory in productTableFilters
                ? (productTableFilters[ProductTableFilterOptions.ProductCategory] as string)
                : undefined
            }
            productCategoryName={
              productTableFilters && ProductTableFilterOptions.ProductCategoryName in productTableFilters
                ? (productTableFilters[ProductTableFilterOptions.ProductCategoryName] as string)
                : undefined
            }
            searchTerm={
              productTableFilters && ProductTableFilterOptions.Search in productTableFilters
                ? (productTableFilters[ProductTableFilterOptions.Search] as string)
                : undefined
            }
            retailer={
              productTableFilters && ProductTableFilterOptions.Retailer in productTableFilters
                ? (productTableFilters[ProductTableFilterOptions.Retailer] as string)
                : undefined
            }
            experimentOnly={
              productTableFilters && ProductTableFilterOptions.Experiment in productTableFilters
                ? productTableFilters[ProductTableFilterOptions.Experiment] === 'true'
                : undefined
            }
            onProductTableFilterChange={onProductTableFilterChange}
          >
            <ProductTableFilters onAuthenticationError={onAuthenticationError} />
            <ProductTableViewerVirtualized
              workspaceId={workspaceId}
              className="max-h-screen w-full"
              onRowClick={handleNavigateToProduct}
              rowActions={rowActions}
              importProductDialog={
                <ProductImportDialog
                  workspaceId={workspaceId ?? undefined}
                  onSuccessfulImport={onSuccessfulImport}
                  onImportError={onAuthenticationError}
                  hideTrigger={true}
                />
              }
              createProductDialog={
                <ProductNewDialog
                  workspaceId={workspaceId ?? undefined}
                  organizationId={organizationId ?? undefined}
                  onSuccessfulCreate={onSuccessfulImport}
                  onAuthenticationError={onAuthenticationError}
                  hideTrigger={true}
                />
              }
              bulkImportDialog={<ProductBulkImportDialog hideTrigger={true} workspaceId={workspaceId ?? undefined} />}
            />
          </ProductTableProvider>
        </div>
      </TabPanel>
    ),
    [
      workspaceId,
      organizationId,
      handleNavigateToProduct,
      rowActions,
      onSuccessfulImport,
      onAuthenticationError,
      onProductTableFilterChange,
      productTableFilters,
    ],
  );

  const [isCollectionDialogOpen, setIsCollectionDialogOpen] = React.useState(false);

  const handleOnSuccessfulCollectionCreate = React.useCallback(
    (collectionId: string) => {
      Sentry.addBreadcrumb({
        message: 'Collection created successfully, invalidating SearchCollectionsInfinite query cache.',
        level: 'info',
        data: { collection_id: collectionId, workspace_id: workspaceId },
      });

      // Mark the collections query as stale without blocking navigation
      void queryUtils.invalidateAllCollectionLists(queryClient, workspaceId ?? '');

      setIsCollectionDialogOpen(false);

      navigateWithState(router, {
        to: '/w/$workspaceSlug/library/collections/$collectionId',
        params: { workspaceSlug, collectionId },
        from: { trail: [{ ...getLibraryBreadcrumb('collections'), search: collectionsSearchParams }] },
      });
    },
    [workspaceId, queryClient, router, collectionsSearchParams, workspaceSlug],
  );

  const { handleRename: handleCollectionRename, handleDelete: handleCollectionDelete } = useCollectionMenuActions({
    workspaceId,
  });

  const [isBenchmarkDialogOpen, setIsBenchmarkDialogOpen] = React.useState(false);

  const handleOpenBenchmarkDialog = React.useCallback(() => setIsBenchmarkDialogOpen(true), []);

  const handleOnSuccessfulBenchmarkCreate = React.useCallback(
    (benchmarkId: string) => {
      setIsBenchmarkDialogOpen(false);

      navigateWithState(router, {
        to: '/w/$workspaceSlug/library/benchmarks/$benchmarkId',
        params: { workspaceSlug, benchmarkId },
        from: { trail: getBenchmarksBreadcrumbTrail(workspaceSlug, benchmarkGridFilters) },
      });
    },
    [router, workspaceSlug, benchmarkGridFilters],
  );

  const collectionsViewSwitch = React.useMemo(
    () =>
      showCustomBenchmarks ? (
        <SegmentedControl
          aria-label="Collections or benchmarks"
          options={[
            { id: CollectionsViewOptions.Collections, label: 'Collections', count: collectionCount },
            { id: CollectionsViewOptions.Benchmarks, label: 'Benchmarks', count: benchmarkCount },
          ]}
          selectedKey={isBenchmarksView ? CollectionsViewOptions.Benchmarks : CollectionsViewOptions.Collections}
          onSelectionChange={(view) => onCollectionsViewChange?.(view)}
          data-testid="library-collections-view-switch"
        />
      ) : null,
    [showCustomBenchmarks, isBenchmarksView, onCollectionsViewChange, collectionCount, benchmarkCount],
  );

  const collectionsPanel = React.useMemo(
    () => (
      <TabPanel id="collections" className="flex flex-col items-center flex-1 w-full mt-0 min-h-0">
        <div className="flex flex-col flex-1 w-full max-w-screen-4xl mx-auto pt-12 px-8 max-sm:pt-8 max-sm:px-4 min-h-0">
          {isCheckingBenchmarksView ? (
            // Waits for the API check, so the view (and its filtered request) only mounts where benchmarks are on.
            <BenchmarkCardGrid benchmarks={[]} isLoading />
          ) : isBenchmarksView ? (
            <>
              <BenchmarkGrid
                workspaceId={workspaceId ?? undefined}
                organizationId={organizationId ?? undefined}
                filters={benchmarkGridFilters}
                onFilterChange={onBenchmarkGridFilterChange}
                leadingFilterContent={collectionsViewSwitch}
                getBenchmarkHref={getBenchmarkHref}
                onNavigateToBenchmark={handleNavigateToBenchmark}
                onCreateBenchmark={handleOpenBenchmarkDialog}
                onAuthenticationError={onAuthenticationError}
              />
              <BenchmarkNewDialog
                workspaceId={workspaceId ?? undefined}
                isOpen={isBenchmarkDialogOpen}
                hideTrigger
                onOpenChange={setIsBenchmarkDialogOpen}
                onSuccessfulCreate={handleOnSuccessfulBenchmarkCreate}
                onAuthenticationError={onAuthenticationError}
              />
            </>
          ) : (
            <CollectionGridProvider
              workspaceId={workspaceId ?? undefined}
              organizationId={organizationId ?? undefined}
              productCategoryId={collectionGridFilters[CollectionGridFilterOptions.ProductCategory]}
              productCategoryName={collectionGridFilters[CollectionGridFilterOptions.ProductCategoryName]}
              imageType={collectionGridFilters[CollectionGridFilterOptions.ImageType] as ImageType | undefined}
              searchTerm={collectionGridFilters[CollectionGridFilterOptions.Search]}
              sortOption={
                collectionGridFilters[CollectionGridFilterOptions.Sort] === CollectionSortOption.Alphabetical
                  ? CollectionSortOption.Alphabetical
                  : CollectionSortOption.DateAdded
              }
              onCollectionGridFilterChange={onCollectionGridFilterChange}
            >
              <CollectionGridFilters
                onAuthenticationError={onAuthenticationError}
                leadingContent={collectionsViewSwitch}
              />
              <div className="flex-1 min-h-0 w-full overflow-y-auto pb-32">
                <CollectionGridViewer
                  onNavigateToCollection={handleNavigateToCollection}
                  className="w-full"
                  showCategory={showCollectionCategory}
                  onCollectionRename={handleCollectionRename}
                  onCollectionDelete={handleCollectionDelete}
                  onAuthenticationError={onAuthenticationError}
                  createCollectionDialog={
                    <CollectionNewDialog
                      workspaceId={workspaceId ?? undefined}
                      organizationId={organizationId ?? undefined}
                      isOpen={isCollectionDialogOpen}
                      onOpenChange={setIsCollectionDialogOpen}
                      onSuccessfulCreate={handleOnSuccessfulCollectionCreate}
                      onAuthenticationError={onAuthenticationError}
                      useIcon={false}
                    />
                  }
                  headerAction={
                    <CollectionNewDialog
                      workspaceId={workspaceId ?? undefined}
                      organizationId={organizationId ?? undefined}
                      isOpen={isCollectionDialogOpen}
                      onOpenChange={setIsCollectionDialogOpen}
                      onSuccessfulCreate={handleOnSuccessfulCollectionCreate}
                      onAuthenticationError={onAuthenticationError}
                      useIcon={false}
                    />
                  }
                />
              </div>
            </CollectionGridProvider>
          )}
        </div>
      </TabPanel>
    ),
    [
      isCheckingBenchmarksView,
      isBenchmarksView,
      showCollectionCategory,
      collectionsViewSwitch,
      benchmarkGridFilters,
      onBenchmarkGridFilterChange,
      workspaceId,
      organizationId,
      collectionGridFilters,
      isCollectionDialogOpen,
      isBenchmarkDialogOpen,
      handleOpenBenchmarkDialog,
      handleOnSuccessfulBenchmarkCreate,
      handleNavigateToCollection,
      getBenchmarkHref,
      handleNavigateToBenchmark,
      handleOnSuccessfulCollectionCreate,
      handleCollectionRename,
      handleCollectionDelete,
      onAuthenticationError,
      onCollectionGridFilterChange,
    ],
  );

  return (
    <>
      <div className={cx('flex flex-col w-full h-full', className)} {...rest}>
        <Tabs
          variant={TabVariant.Underline}
          selectedKey={selectedTab}
          size={TabSize.Md}
          onSelectionChange={handleTabChange}
          className="flex flex-col w-full h-full"
        >
          <TabList className="w-fit px-12 max-sm:px-8 gap-6" aria-label="Library tabs">
            <Tab id="products" data-testid="tab-products">
              Product Pages
            </Tab>
            <Tab id="collections" data-testid="tab-collections">
              Collections
            </Tab>
            <Tab id="assets" data-testid="tab-assets">
              Assets
            </Tab>
          </TabList>
          <Separator />
          {assetsPanel}
          {collectionsPanel}
          {productsPanel}
        </Tabs>
      </div>
      {actionModal}
    </>
  );
}

export { CollectionsViewOptions, LibraryPage, LibraryTabOptions };
